import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Plus, Users, Store, UserCircle, LogOut, BookOpen, Wallet, ShieldCheck } from "lucide-react";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import WalletBadge from "./WalletBadge";

const NavItem = ({ to, icon: Icon, children, testid }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
        isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
      }`
    }
  >
    <Icon className="w-4 h-4" />
    {children}
  </NavLink>
);

export default function DashboardLayout({ children, title, action }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="dashboard-layout">
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0B1B3D] text-white flex flex-col p-5">
        <Link to="/dashboard" className="flex items-center gap-2 mb-10" data-testid="sidebar-brand">
          <div className="w-8 h-8 rounded-md bg-white grid place-items-center"><span className="font-display font-black text-[#0B1B3D]">K</span></div>
          <span className="font-display font-extrabold text-lg tracking-tight">Konzenzo</span>
        </Link>
        <nav className="flex flex-col gap-1.5">
          <NavItem to="/dashboard" icon={LayoutDashboard} testid="nav-dashboard">{t("Dashboard")}</NavItem>
          <NavItem to="/sessions/new" icon={Plus} testid="nav-new-session">{t("New session")}</NavItem>
          <NavItem to="/agents" icon={Users} testid="nav-agents">{t("Expert Agents")}</NavItem>
          <NavItem to="/marketplace" icon={Store} testid="nav-marketplace">{t("Marketplace")}</NavItem>
          <NavItem to="/billing" icon={Wallet} testid="nav-billing">{t("Billing")}</NavItem>
          <NavItem to="/profile" icon={UserCircle} testid="nav-profile">{t("Profile")}</NavItem>
          {user?.is_admin && <NavItem to="/admin" icon={ShieldCheck} testid="nav-admin">{t("Admin")}</NavItem>}
        </nav>
        <div className="mt-auto pt-6 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-white/20" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-white/10 grid place-items-center"><span className="font-display font-bold">{user?.name?.[0] || "U"}</span></div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{user?.name || "User"}</p>
              <p className="text-xs text-slate-400 truncate font-mono-kz">{user?.email}</p>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={handleLogout} className="w-full flex items-center gap-2 text-sm text-slate-300 hover:text-white px-2 py-1.5 rounded-md hover:bg-white/5">
            <LogOut className="w-4 h-4" /> {t("Sign out")}
          </button>
        </div>
      </aside>
      <main className="ml-64">
        <div className="border-b border-slate-200 bg-white sticky top-0 z-30">
          <div className="px-8 py-5 flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl font-extrabold text-[#0B1B3D] tracking-tight">{title}</h1>
            </div>
            <div className="flex items-center gap-3">
              <WalletBadge />
              {action}
            </div>
          </div>
        </div>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}
